// Strava認証UI コンポーネント

import React, { useState, useEffect } from 'react';
import { User, LogOut, AlertCircle, Loader } from 'lucide-react';
import { AuthManager } from '../utils/auth';
import type { AuthState } from '../utils/auth/types';

interface StravaAuthProps {
  onAuthStateChange?: (authState: AuthState) => void;
}

const StravaAuth: React.FC<StravaAuthProps> = ({ onAuthStateChange }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    token: null,
    athlete: null,
    error: null
  });

  const authManager = AuthManager.getInstance();

  useEffect(() => {
    initializeAuth();
  }, []);

  useEffect(() => {
    onAuthStateChange?.(authState);
  }, [authState, onAuthStateChange]);

  const initializeAuth = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      if (authManager.isAuthenticated()) {
        const athlete = await authManager.getAuthenticatedAthlete();
        const token = authManager.getCurrentToken();
        
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          token,
          athlete,
          error: null
        });
      } else {
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false,
          token: null,
          athlete: null
        }));
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        token: null,
        athlete: null,
        error: error instanceof Error ? error.message : '認証の初期化に失敗しました'
      });
    }
  };

  const handleLogin = () => {
    try {
      const authUrl = authManager.generateAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'ログインURLの生成に失敗しました'
      }));
    }
  };

  const handleLogout = () => {
    authManager.logout();
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      token: null,
      athlete: null,
      error: null
    });
  };

  if (authState.isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
        <Loader className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-blue-600 text-sm">認証状態を確認中...</span>
      </div>
    );
  }

  if (authState.error) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg">
        <AlertCircle className="w-4 h-4 text-red-600" />
        <span className="text-red-600 text-sm">{authState.error}</span>
        <button
          onClick={initializeAuth}
          className="ml-2 text-red-600 hover:text-red-800 text-sm underline"
        >
          再試行
        </button>
      </div>
    );
  }

  if (authState.isAuthenticated && authState.athlete) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 bg-green-50 rounded-lg">
        <div className="flex items-center gap-2">
          <img
            src={authState.athlete.profile_medium}
            alt={`${authState.athlete.firstname} ${authState.athlete.lastname}`}
            className="w-8 h-8 rounded-full"
          />
          <div className="flex flex-col">
            <span className="text-green-800 text-sm font-medium">
              {authState.athlete.firstname} {authState.athlete.lastname}
            </span>
            <span className="text-green-600 text-xs">
              Strava 連携済み
            </span>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1 px-3 py-1 text-green-700 hover:text-green-900 hover:bg-green-100 rounded transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm">ログアウト</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
      <User className="w-4 h-4 text-gray-600" />
      <span className="text-gray-600 text-sm mr-2">Strava連携が必要です</span>
      <button
        onClick={handleLogin}
        className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors"
      >
        <span className="text-sm font-medium">Strava でログイン</span>
      </button>
    </div>
  );
};

export default StravaAuth;